'use client';

import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';
import type { Timeframe, SportFilter, BetTypeFilter } from './ReportingFilters';

interface WinRatePoint {
  month: string;
  winRate: number;
}

interface WinRateTrendChartProps {
  timeframe: Timeframe;
  sport: SportFilter;
  betType: BetTypeFilter;
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{value: number; name: string}>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 shadow-lg">
      <p className="text-xs font-semibold text-[hsl(var(--foreground))] mb-1">{label}</p>
      <p className="font-mono font-bold text-base text-blue-600 tabular-nums">{payload[0].value}% win rate</p>
    </div>
  );
}

function getDateFilter(timeframe: Timeframe): string | null {
  const now = new Date();
  if (timeframe === '7d') { now.setDate(now.getDate() - 7); return now.toISOString(); }
  if (timeframe === '30d') { now.setDate(now.getDate() - 30); return now.toISOString(); }
  if (timeframe === '90d') { now.setDate(now.getDate() - 90); return now.toISOString(); }
  if (timeframe === 'ytd') { return new Date(now.getFullYear(), 0, 1).toISOString(); }
  return null;
}

export default function WinRateTrendChart({ timeframe, sport, betType }: WinRateTrendChartProps) {
  const [winRateData, setWinRateData] = useState<WinRatePoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        let query = supabase
          .from('picks')
          .select('posted_at, result')
          .neq('result', 'pending')
          .order('posted_at', { ascending: true });

        if (sport !== 'all') query = query.eq('sport', sport);
        if (betType !== 'all') query = query.eq('bet_type', betType);
        const dateFrom = getDateFilter(timeframe);
        if (dateFrom) query = query.gte('posted_at', dateFrom);

        const { data, error } = await query;
        if (error) throw error;

        const byMonth: Record<string, { wins: number; losses: number; pushes: number }> = {};
        (data || []).forEach((p) => {
          const month = new Date(p.posted_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          if (!byMonth[month]) byMonth[month] = { wins: 0, losses: 0, pushes: 0 };
          if (p.result === 'win') byMonth[month].wins++;
          else if (p.result === 'loss') byMonth[month].losses++;
          else if (p.result === 'push') byMonth[month].pushes++;
        });

        let totalWins = 0;
        let totalLosses = 0;
        const points: WinRatePoint[] = Object.entries(byMonth).map(([month, d]) => {
          totalWins += d.wins;
          totalLosses += d.losses;
          const winRate = totalWins + totalLosses > 0
            ? parseFloat(((totalWins / (totalWins + totalLosses)) * 100).toFixed(1))
            : 0;
          return { month, winRate };
        });

        setWinRateData(points);
      } catch {
        setWinRateData([]);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [timeframe, sport, betType]);

  const currentRate = winRateData.length > 0 ? winRateData[winRateData.length - 1].winRate : 0;

  return (
    <div className="stat-card mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">Win Rate Trend</h2>
          <p className="text-xs text-[hsl(var(--foreground-muted))] mt-0.5">Cumulative win rate over time</p>
        </div>
        {!loading && winRateData.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200">
            <span className="font-mono text-sm font-bold text-blue-600 tabular-nums">{currentRate}%</span>
            <span className="text-xs text-[hsl(var(--foreground-muted))]">current</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[200px] gap-2">
          <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading chart...</span>
        </div>
      ) : winRateData.length === 0 ? (
        <div className="flex items-center justify-center h-[200px]">
          <p className="text-sm text-[hsl(var(--foreground-muted))]">No data yet. Chart will appear once picks are graded.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={winRateData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 13%, 90%)" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
            <ReferenceLine y={52.4} stroke="hsl(38, 85%, 45%)" strokeDasharray="4 4" strokeOpacity={0.7} label={{ value: 'break-even', fill: 'hsl(38, 85%, 45%)', fontSize: 10, fontFamily: 'JetBrains Mono' }} />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="winRate" stroke="hsl(210, 90%, 50%)" strokeWidth={2.5} dot={{ r: 3, fill: 'hsl(210, 90%, 50%)', stroke: 'white', strokeWidth: 2 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}